import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Headphones, Clock, User, Check, CheckCheck, RefreshCw, Bell, BellOff, Bot, UserCircle, Sparkles } from 'lucide-react';
import api from '../services/api';
import pushService from '../services/pushNotificationService';
import { toast } from 'sonner';

// AI Knowledge Base about the platform
const PLATFORM_KNOWLEDGE = {
    deposit: "To deposit funds:\n1. Go to the Deposit page from the sidebar\n2. Select your preferred cryptocurrency (BTC, ETH, USDT, or USDC)\n3. Copy the wallet address shown or scan the QR code\n4. Send funds from your external wallet to this address\n5. Deposits are usually confirmed within 10-30 minutes depending on the network",
    withdraw: "To withdraw funds:\n1. Go to the Withdraw page\n2. Select the currency you want to withdraw\n3. Enter the destination wallet address\n4. Enter the amount you want to withdraw\n5. Confirm the transaction\n\nNote: If you have Account Manager enabled, you may need to connect your bank through iDEAL via Paybis for EUR withdrawals.",
    kyc: "KYC (Know Your Customer) verification:\n1. Go to Profile > Verification tab\n2. Upload your ID document (passport, driver's license, or national ID)\n3. Take a selfie for identity verification\n4. Submit proof of address (utility bill or bank statement)\n5. Our team reviews submissions within 24-48 hours\n\nHigher KYC levels unlock higher withdrawal limits.",
    trading: "Trading on our platform:\n1. Go to the Portfolio page to view your holdings\n2. You can open long or short positions with leverage\n3. Our CyberAI bot can trade automatically on your behalf\n4. Monitor your P&L in real-time on the dashboard\n5. Use stop-loss and take-profit orders to manage risk",
    bot: "CyberAI Trading Bot:\n1. The bot automatically trades based on market conditions\n2. It uses advanced algorithms for optimal entry and exit\n3. You can enable/disable the bot from your Portfolio\n4. The bot maintains a high win rate through smart position sizing\n5. All bot trades are visible in your trade history",
    fees: "Our fee structure:\n• Trading fees: 0.1% per trade\n• Withdrawal fees: Network fees only\n• No deposit fees\n• No monthly subscription fees\n• Bot trading: No additional fees",
    security: "Platform security:\n• 2FA (Two-Factor Authentication) available\n• Cold storage for majority of funds\n• SSL encryption for all data\n• Regular security audits\n• Private keys are encrypted",
    support: "Support options:\n• Live chat (you're using it now!)\n• Email support at support@cybernetics.com\n• Response time: Usually within 24 hours\n• For urgent issues, request to speak with a human agent",
    account: "Account management:\n• Update your profile in the Profile section\n• Change password in Security settings\n• Enable 2FA for extra security\n• Set your preferred fiat currency\n• Manage notification preferences"
};

const SupportChat = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushLoading, setPushLoading] = useState(false);
    const [chatMode, setChatMode] = useState('ai'); // 'ai' or 'human'
    const [waitingForHuman, setWaitingForHuman] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        const checkPushStatus = async () => {
            await pushService.init();
            const subscribed = await pushService.isSubscribed();
            setPushEnabled(subscribed);
        };
        checkPushStatus();
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchMessages();
            const interval = setInterval(fetchMessages, 10000);
            return () => clearInterval(interval);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 300);
        }
    }, [isOpen]);

    // Show welcome message when chat opens
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            const welcomeMsg = {
                id: 'welcome',
                sender: 'bot',
                message: "👋 Hi! I'm CyberBot, your AI assistant. I can help you with:\n\n• Deposits & Withdrawals\n• KYC Verification\n• Trading & Bot setup\n• Account settings\n• Platform features\n\nAsk me anything, or type 'human' to speak with a real support agent!",
                created_at: new Date().toISOString()
            };
            setMessages([welcomeMsg]);
        }
    }, [isOpen]);

    const fetchMessages = async () => {
        try {
            setLoading(true);
            const response = await api.get('/chat/messages');
            const messagesData = response.data?.messages || response.data || [];
            
            // Keep welcome message and add fetched messages
            const welcomeMsg = messages.find(m => m.id === 'welcome');
            const fetchedMsgs = Array.isArray(messagesData) ? messagesData : [];
            
            if (welcomeMsg && fetchedMsgs.length === 0) {
                setMessages([welcomeMsg]);
            } else if (fetchedMsgs.length > 0) {
                setMessages(fetchedMsgs);
            }
            
            const unread = fetchedMsgs.filter(m => m.sender === 'admin' && !m.read).length;
            setUnreadCount(unread);
            
            // Check if there's an admin response
            if (fetchedMsgs.some(m => m.sender === 'admin')) {
                setWaitingForHuman(false);
                setChatMode('human');
            }
        } catch (error) {
            console.error('Failed to fetch messages:', error);
        } finally {
            setLoading(false);
        }
    };

    const togglePushNotifications = async () => {
        setPushLoading(true);
        try {
            if (pushEnabled) {
                await pushService.unsubscribe();
                setPushEnabled(false);
                toast.success('Push notifications disabled');
            } else {
                const subscription = await pushService.subscribe();
                if (subscription) {
                    setPushEnabled(true);
                    toast.success('Push notifications enabled!');
                } else {
                    toast.error('Failed to enable push notifications');
                }
            }
        } catch (error) {
            toast.error('Failed to toggle push notifications');
        } finally {
            setPushLoading(false);
        }
    };

    // AI Response Generator
    const generateAIResponse = (userMessage) => {
        const msg = userMessage.toLowerCase();
        
        // Check for human request
        if (msg.includes('human') || msg.includes('agent') || msg.includes('real person') || msg.includes('speak to someone')) {
            setChatMode('human');
            setWaitingForHuman(true);
            return "I'll connect you with a human support agent. Your message has been sent to our team. They typically respond within a few hours. In the meantime, feel free to ask me any questions!\n\n🟢 Status: Waiting for human agent...";
        }
        
        // Knowledge-based responses
        if (msg.includes('deposit') || msg.includes('add fund') || msg.includes('send money')) {
            return PLATFORM_KNOWLEDGE.deposit;
        }
        if (msg.includes('withdraw') || msg.includes('cash out') || msg.includes('take money')) {
            return PLATFORM_KNOWLEDGE.withdraw;
        }
        if (msg.includes('kyc') || msg.includes('verify') || msg.includes('verification') || msg.includes('identity')) {
            return PLATFORM_KNOWLEDGE.kyc;
        }
        if (msg.includes('trade') || msg.includes('trading') || msg.includes('buy') || msg.includes('sell') || msg.includes('position')) {
            return PLATFORM_KNOWLEDGE.trading;
        }
        if (msg.includes('bot') || msg.includes('auto') || msg.includes('cyberai') || msg.includes('algorithm')) {
            return PLATFORM_KNOWLEDGE.bot;
        }
        if (msg.includes('fee') || msg.includes('cost') || msg.includes('charge') || msg.includes('price')) {
            return PLATFORM_KNOWLEDGE.fees;
        }
        if (msg.includes('security') || msg.includes('safe') || msg.includes('protect') || msg.includes('2fa') || msg.includes('password')) {
            return PLATFORM_KNOWLEDGE.security;
        }
        if (msg.includes('support') || msg.includes('help') || msg.includes('contact') || msg.includes('email')) {
            return PLATFORM_KNOWLEDGE.support;
        }
        if (msg.includes('account') || msg.includes('profile') || msg.includes('setting')) {
            return PLATFORM_KNOWLEDGE.account;
        }
        if (msg.includes('hello') || msg.includes('hi') || msg.includes('hey')) {
            return "Hello! 👋 How can I help you today? I can assist with deposits, withdrawals, trading, KYC verification, and more. Just ask!";
        }
        if (msg.includes('thank')) {
            return "You're welcome! 😊 Is there anything else I can help you with?";
        }
        
        // Default response
        return "I'm not sure I understand that question. Here are some things I can help with:\n\n• How do I deposit?\n• How do I withdraw?\n• KYC verification\n• Trading help\n• Bot setup\n• Account settings\n\nOr type 'human' to speak with a support agent.";
    };

    const handleSend = async () => {
        if (!inputValue.trim() || sending) return;

        const userMessage = inputValue.trim();
        setSending(true);
        setInputValue('');

        // Add user message locally
        const userMsg = {
            id: `user-${Date.now()}`,
            sender: 'user',
            message: userMessage,
            created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, userMsg]);

        // If in AI mode, generate response locally first
        if (chatMode === 'ai' && !waitingForHuman) {
            setIsTyping(true);
            
            // Simulate typing delay
            setTimeout(() => {
                const aiResponse = generateAIResponse(userMessage);
                const botMsg = {
                    id: `bot-${Date.now()}`,
                    sender: 'bot',
                    message: aiResponse,
                    created_at: new Date().toISOString()
                };
                setMessages(prev => [...prev, botMsg]);
                setIsTyping(false);
            }, 1000 + Math.random() * 1000);
        }

        // Also send to backend for human support
        try {
            await api.post('/chat/messages', { 
                message: userMessage,
                request_human: chatMode === 'human' || userMessage.toLowerCase().includes('human')
            });
        } catch (error) {
            console.error('Failed to send message to backend:', error);
        } finally {
            setSending(false);
        }
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diff = now - date;
        
        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const quickReplies = [
        'How do I deposit?',
        'Withdrawal help',
        'KYC verification',
        'Trading bot',
        'Speak to human'
    ];

    const handleQuickReply = (text) => {
        setInputValue(text);
        inputRef.current?.focus();
    };

    const switchToHuman = () => {
        setChatMode('human');
        setWaitingForHuman(true);
        const switchMsg = {
            id: `bot-switch-${Date.now()}`,
            sender: 'bot',
            message: "I've notified our support team. A human agent will respond to your messages soon. Average response time is within a few hours.\n\n🟢 Status: Waiting for human agent...",
            created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, switchMsg]);
        
        // Notify backend
        api.post('/chat/messages', { 
            message: '[User requested human support]',
            request_human: true
        }).catch(console.error);
    };

    return (
        <>
            {/* Chat Button */}
            <motion.button
                onClick={() => {
                    setIsOpen(!isOpen);
                    if (!isOpen) setUnreadCount(0);
                }}
                className="fixed bottom-6 right-6 z-[9999] w-14 h-14 bg-gradient-to-br from-[#5BA4A4] to-[#4A8F8F] rounded-full hidden md:flex items-center justify-center shadow-[0_0_20px_rgba(91,164,164,0.5)] hover:shadow-[0_0_30px_rgba(91,164,164,0.7)] transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                data-testid="support-chat-btn"
            >
                {isOpen ? (
                    <X size={24} className="text-black" />
                ) : (
                    <>
                        <MessageCircle size={24} className="text-black" />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white animate-pulse">
                                {unreadCount}
                            </span>
                        )}
                    </>
                )}
            </motion.button>

            {/* Chat Window */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="fixed bottom-36 md:bottom-24 right-6 z-[9999] w-[400px] max-w-[calc(100vw-3rem)] bg-[#0A0A0A] border border-[rgba(91,164,164,0.3)] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
                        style={{ height: '560px', maxHeight: 'calc(100vh - 150px)' }}
                        data-testid="support-chat-window"
                    >
                        {/* Header */}
                        <div className="p-4 border-b border-[rgba(91,164,164,0.2)]" style={{ background: 'linear-gradient(135deg, rgba(91,164,164,0.15) 0%, transparent 100%)' }}>
                            <div className="flex items-center gap-3">
                                <div className="relative">
                                    <div className={`w-11 h-11 rounded-full flex items-center justify-center ${chatMode === 'ai' ? 'bg-gradient-to-br from-purple-500/30 to-blue-500/30' : 'bg-[#5BA4A4]/20'}`}>
                                        {chatMode === 'ai' ? (
                                            <Sparkles size={22} className="text-purple-400" />
                                        ) : (
                                            <Headphones size={22} className="text-[#5BA4A4]" />
                                        )}
                                    </div>
                                    <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-[#0a0a0a] ${waitingForHuman ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'}`} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-bold text-white text-sm flex items-center gap-2">
                                        {chatMode === 'ai' ? 'CyberBot AI' : 'Support Team'}
                                        {chatMode === 'ai' && <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded">AI</span>}
                                    </h3>
                                    <p className="text-xs text-green-400">
                                        {waitingForHuman ? '⏳ Waiting for agent...' : chatMode === 'ai' ? '⚡ Instant responses' : '🟢 Online'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1">
                                    {chatMode === 'ai' && (
                                        <button
                                            onClick={switchToHuman}
                                            className="px-2 py-1 text-[10px] bg-white/10 hover:bg-white/20 rounded-lg text-gray-300 transition-colors"
                                            title="Talk to human"
                                        >
                                            <UserCircle size={14} />
                                        </button>
                                    )}
                                    <button
                                        onClick={togglePushNotifications}
                                        disabled={pushLoading}
                                        className={`p-2 rounded-lg transition-colors ${pushEnabled ? 'bg-[#5BA4A4]/20 text-[#5BA4A4]' : 'hover:bg-white/10 text-gray-400'}`}
                                    >
                                        {pushLoading ? <RefreshCw size={14} className="animate-spin" /> : pushEnabled ? <Bell size={14} /> : <BellOff size={14} />}
                                    </button>
                                    <button onClick={fetchMessages} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                                        <RefreshCw size={14} className={`text-gray-400 ${loading ? 'animate-spin' : ''}`} />
                                    </button>
                                    <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                                        <X size={14} className="text-gray-400" />
                                    </button>
                                </div>
                            </div>
                            
                            {/* Mode Toggle */}
                            <div className="flex gap-2 mt-3">
                                <button
                                    onClick={() => setChatMode('ai')}
                                    className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-all ${
                                        chatMode === 'ai' 
                                            ? 'bg-gradient-to-r from-purple-500/30 to-blue-500/30 text-purple-300 border border-purple-500/30' 
                                            : 'bg-white/5 text-gray-500 hover:bg-white/10'
                                    }`}
                                >
                                    <Bot size={12} className="inline mr-1" /> AI Assistant
                                </button>
                                <button
                                    onClick={switchToHuman}
                                    className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-all ${
                                        chatMode === 'human' 
                                            ? 'bg-[#5BA4A4]/20 text-[#5BA4A4] border border-[#5BA4A4]/30' 
                                            : 'bg-white/5 text-gray-500 hover:bg-white/10'
                                    }`}
                                >
                                    <UserCircle size={12} className="inline mr-1" /> Human Agent
                                </button>
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {messages.map((msg, index) => (
                                <motion.div
                                    key={msg.id || index}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.02 }}
                                    className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div className={`flex items-end gap-2 max-w-[85%] ${msg.sender === 'user' ? 'flex-row-reverse' : ''}`}>
                                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                                            msg.sender === 'user' ? 'bg-[#5BA4A4]/20' : 
                                            msg.sender === 'bot' ? 'bg-gradient-to-br from-purple-500/30 to-blue-500/30' : 
                                            'bg-purple-500/20'
                                        }`}>
                                            {msg.sender === 'user' ? (
                                                <User size={14} className="text-[#5BA4A4]" />
                                            ) : msg.sender === 'bot' ? (
                                                <Sparkles size={14} className="text-purple-400" />
                                            ) : (
                                                <Headphones size={14} className="text-purple-400" />
                                            )}
                                        </div>
                                        
                                        <div className={`rounded-2xl px-4 py-2.5 ${
                                            msg.sender === 'user'
                                                ? 'bg-[#5BA4A4] text-black rounded-br-md'
                                                : msg.sender === 'bot'
                                                ? 'bg-gradient-to-br from-purple-500/10 to-blue-500/10 text-white rounded-bl-md border border-purple-500/20'
                                                : 'bg-[#1a1a1a] text-white rounded-bl-md border border-[rgba(91,164,164,0.15)]'
                                        }`}>
                                            {msg.sender === 'admin' && msg.admin_name && (
                                                <p className="text-[10px] text-purple-400 font-semibold mb-1">{msg.admin_name}</p>
                                            )}
                                            {msg.sender === 'bot' && (
                                                <p className="text-[10px] text-purple-400 font-semibold mb-1">CyberBot AI</p>
                                            )}
                                            <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                                            <div className={`flex items-center gap-1 mt-1.5 ${msg.sender === 'user' ? 'justify-end' : ''}`}>
                                                <span className={`text-[10px] ${msg.sender === 'user' ? 'text-black/60' : 'text-gray-500'}`}>
                                                    {formatTime(msg.created_at)}
                                                </span>
                                                {msg.sender === 'user' && (
                                                    msg.read ? <CheckCheck size={12} className="text-black/60" /> : <Check size={12} className="text-black/60" />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                            
                            {/* Typing indicator */}
                            {isTyping && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="flex items-center gap-2"
                                >
                                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500/30 to-blue-500/30 flex items-center justify-center">
                                        <Sparkles size={14} className="text-purple-400" />
                                    </div>
                                    <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-2xl px-4 py-3 border border-purple-500/20">
                                        <div className="flex gap-1">
                                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                            
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Quick Replies */}
                        {messages.length <= 1 && (
                            <div className="px-4 pb-2">
                                <div className="flex flex-wrap gap-1.5">
                                    {quickReplies.map((reply, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleQuickReply(reply)}
                                            className="text-[11px] px-2.5 py-1 bg-[rgba(91,164,164,0.1)] border border-[rgba(91,164,164,0.3)] rounded-full text-[#5BA4A4] hover:bg-[rgba(91,164,164,0.2)] transition-colors"
                                        >
                                            {reply}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Input */}
                        <div className="p-4 border-t border-[rgba(91,164,164,0.2)] bg-black/30">
                            <div className="flex gap-2">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                                    placeholder={chatMode === 'ai' ? "Ask me anything..." : "Type your message..."}
                                    className="flex-1 bg-[#1a1a1a] border border-[rgba(91,164,164,0.2)] rounded-xl text-white text-sm px-4 py-3 focus:outline-none focus:border-[#5BA4A4] placeholder-gray-500 transition-colors"
                                    data-testid="support-chat-input"
                                />
                                <button
                                    onClick={handleSend}
                                    disabled={!inputValue.trim() || sending}
                                    className="px-4 bg-gradient-to-r from-[#5BA4A4] to-[#4A8F8F] rounded-xl text-black hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                    data-testid="support-chat-send"
                                >
                                    <Send size={18} className={sending ? 'animate-pulse' : ''} />
                                </button>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                                <div className="flex items-center gap-1 text-[10px] text-gray-500">
                                    {chatMode === 'ai' ? (
                                        <>
                                            <Sparkles size={10} className="text-purple-400" />
                                            <span>AI-powered instant support</span>
                                        </>
                                    ) : (
                                        <>
                                            <Clock size={10} />
                                            <span>Human agents respond within hours</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default SupportChat;
